import React, { useContext, useEffect } from "react";
import { AuthContext } from "../../../../../../../../context/AuthContext";
import { useAutoImportFormContext } from "../../Context/AutoImportFormContext";
import styles from "../AddEditAutoImportConfigurationsModal.module.css";
import { LRequiredInput } from "./CommonComponents/LRequiredInput";
import { FormGrid } from "./CommonComponents/FormGrid";
import { LFormInput } from "@loffa/gpp-modules";
import { User } from "@appTypes/user";
import { ProductType } from "@appTypes/productType";
import { TemplateMap } from "@appTypes/dataTemplateMap";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";
import {Client as FormattedClient} from "@appTypes/clientTypes"
import { AutoImportType } from "@appTypes/autoImportType";
const GATEWAY_BASE_URL = process.env.REACT_APP_API_GATEWAY_BASE;
interface ClientResponse {
  clientGuid: string;
  name: string;
  isActive: boolean;
  dtcNumber: string;
  formattedName: string;
  id: number;
}
interface ClientProductConfig {
  clientProduct: { clientProductGuid: string };
}

function GeneralInfoForm() {
  const { formData, setError, generalInfoDropdownOptions, updateGeneralInfoDropdownOptions, updateStepData } = useAutoImportFormContext();
  const authContext = useContext(AuthContext);
  
  const { generalInformation } = formData;
  const token = authContext.authState().accessToken.accessToken;

   //fetches active clients on initial mount
   useEffect(() => {
    const fetchClientList = async () => {
      try {
        const response = await fetch(
          `${GATEWAY_BASE_URL}/multitenancy/api/Clients/Clients`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch clients.");
        const result: ClientResponse[] = await response.json();
        const clients: FormattedClient[] = result
          .filter((c) => c.isActive)
          .map((e) => ({ formattedName: e.formattedName, clientValuePayload: {
              dtcNumber: e.dtcNumber,
              clientGuid: e.clientGuid,
            }}));
        updateGeneralInfoDropdownOptions({ activeClientList: clients });
        setError(null);
      } catch (error: any) {
        setError("Error fetching Client list: " + error.message);
      }
    };
    fetchClientList();
  }, [token, setError, updateGeneralInfoDropdownOptions]);

  const onClientSelect = (e: KendoComboBoxEvent) => {
    const client = e.target.value as FormattedClient;
    updateStepData('generalInformation',{ client, productType: undefined, clientProductGuid: "", autoImportType: undefined, templateMap: undefined });
    updateGeneralInfoDropdownOptions({ clientProducts: [], autoImportTypes: [], templateMaps: [] });//clears dependent dropdown data
    fetchClientProducts(client);
  };

  // Handler: When a client product is selected
  const onProductSelect = (e: KendoComboBoxEvent) => {
    const product = e.target.value as ProductType;
    updateStepData('generalInformation', { 
      productType: product, 
      autoImportType: undefined, 
      mapId: null, 
      templateMap: null 
    });
    updateGeneralInfoDropdownOptions({ autoImportTypes: [], templateMaps: [] });
    fetchAutoImportTypes(product);
    fetchClientProductConfig(product);
  };

  const onAutoImportSelect = (e: KendoComboBoxEvent) => {
    const autoImportType = e.target.value as AutoImportType;
    updateStepData('generalInformation', { 
      autoImportType, 
      mapId: autoImportType?.templateId || null, 
      templateMap: null 
    });
    updateGeneralInfoDropdownOptions({ templateMaps: [] });
    if(autoImportType){
      fetchTemplateMaps(autoImportType);
    }  
    };

  const onTemplateMapSelect = (e: KendoComboBoxEvent) => {
    const templateMap = e.target.value as TemplateMap;
    updateStepData('generalInformation', {
      templateMap,
      mapId: templateMap?.templateMapId || generalInformation.autoImportType?.templateId,//defaults to auto import template id if no mapper is selected
    });
  };

  const onProductUserSelect = (e: KendoComboBoxEvent) => {
    const clientProductUser = e.target.value as User;
    updateStepData('generalInformation', {
      clientProductUser: clientProductUser
    });
  };

  //called after user selects a client - fetches all products assigned to the client
  const fetchClientProducts = async (client: FormattedClient) => {
    if(!client) return
    try {
      const url = `${GATEWAY_BASE_URL}/multitenancy/api/Clients/${client.clientValuePayload.clientGuid}/Products`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch client products.");
      const result: ProductType[] = await response.json();
      updateGeneralInfoDropdownOptions({ clientProducts: result });
      setError(null);
    } catch (error: any) {
      setError("Error fetching Client Product list: " + error.message);
    }
  };
  //called after user selects both a client and product - fetches ClientProductGuid
  const fetchClientProductConfig = async (product: ProductType) => {
    if(!product) return
    try {
      const url = `${GATEWAY_BASE_URL}/multitenancy/api/Clients/${generalInformation.client?.clientValuePayload.clientGuid}/Products/${product?.productGuid}/Configuration`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch client product config.");
      const result: ClientProductConfig = await response.json();
      updateStepData('generalInformation', { clientProductGuid: result.clientProduct.clientProductGuid });
      setError(null);
      fetchClientProductUsers(product)
    } catch (error: any) {
      setError("Error fetching Client Product Config: " + error.message);
    }
  };

  //autoimport types are data templates from the data manager
  const fetchAutoImportTypes = async (product: ProductType) => {
    if(!product) return
    try {
      const url = `${GATEWAY_BASE_URL}/datamanager/api/Template/DataTemplate/${product.productTypeId}/Product`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch auto import types.");
      const result: AutoImportType[] = await response.json();
      updateGeneralInfoDropdownOptions({ autoImportTypes: result });
      setError(null);
    } catch (error: any) {
      setError("Failed to fetch auto import types: " + error.message);
    }
  };

  //if the client has any template maps, a list of them will be fetched and rendered after user selects the auto import type
  const fetchTemplateMaps = async (autoImportType: AutoImportType) => {
    if(!autoImportType) return
    try {
      if (!generalInformation.client || !generalInformation.productType) return;
      const url = `${GATEWAY_BASE_URL}/datamanager/api/Template/TemplateMap/LoffaTemplate/${autoImportType.templateId}/Client/${generalInformation.client.clientValuePayload.clientGuid}/Product/${generalInformation.productType.productTypeId}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error("Failed to fetch template maps.");
      const result: TemplateMap[] = await response.json();
      updateGeneralInfoDropdownOptions({ templateMaps: result });
      setError(null);
    } catch (error: any) {
    }
  };

  const fetchClientProductUsers= async (product: ProductType) => {
    try {
      if (!generalInformation.client || !product) return;
      const clientGuid = formData.generalInformation.client?.clientValuePayload.clientGuid
      const url = `${GATEWAY_BASE_URL}/multitenancy/api/Clients/${clientGuid}/Products/${product.productGuid}/Users`;
      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`},
      });
      if (!response.ok) throw new Error("Failed to users assigned to selected client products.");
      const result: User[] = await response.json();
      updateGeneralInfoDropdownOptions({ clientProductUsers: result });
      setError(null);
    } catch (error: any) {
      setError("Failed to users assigned to selected client products: " + error.message);
    }
  };

  const inputs = [
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="Description"
          input={{
            name: "Description",
            placeholder: "Enter Description",
            value: generalInformation.description || "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateStepData('generalInformation', { description: e.target.value })}}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          required
          labelName="Industry Identifier"
          input={{
            name: "Industry Identifier",
            placeholder: "Enter Industry Identifier",
            value: generalInformation.industryIdentifier || "",
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => updateStepData('generalInformation', { industryIdentifier: e.target.value })}}
        />
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Client"
            labelAlign="top"
            required
            comboBox={{
              data: generalInfoDropdownOptions.activeClientList,
              textField: "formattedName",
              valueField: "clientValuePayload",
              dataItemKey: "clientValuePayload.clientGuid",
              value: generalInformation.client || null,
              onChange: onClientSelect,
              placeholder: "-- Choose a Client --",
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Product"
            labelAlign="top"
            required
            comboBox={{
              data: generalInfoDropdownOptions.clientProducts,
              textField: "name",
              dataItemKey: "productGuid",
              value: generalInformation.productType || null,
              onChange: onProductSelect,
              placeholder: "-- Choose Client Product --",
              disabled: !generalInformation.client,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Execute on Behalf of User"
            labelAlign="top"
            required
            comboBox={{
              data: generalInfoDropdownOptions.clientProductUsers,
              textField: "getFullName",
              dataItemKey: "userGuid",
              value: generalInformation.clientProductUser || null,
              onChange: onProductUserSelect,
              placeholder: "-- Choose a User --",
              disabled: !generalInformation.productType,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Auto Import Type"
            labelAlign="top"
            required
            comboBox={{
              data: generalInfoDropdownOptions.autoImportTypes,
              textField: "name",
              dataItemKey: "templateId",
              value: generalInformation.autoImportType || null,
              onChange: onAutoImportSelect,
              placeholder: "-- Choose Auto Import Type --",
              disabled: !generalInformation.clientProductUser,
            }}
          />
        </div>
      ),
    },
    {
      colSpan: 6,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LFormInput
            type="combobox"
            labelName="Data Mapper (optional)"
            labelAlign="top"
            comboBox={{
              data: generalInfoDropdownOptions.templateMaps,
              textField: "name",
              dataItemKey: "templateMapId",
              value: generalInformation.templateMap || null,
              onChange: onTemplateMapSelect,
              placeholder: "-- Choose Template Map --",
              disabled: !generalInformation.autoImportType,
            }}
          />
        </div>
      ),
    },
  ];

  return <FormGrid items={inputs} />;
}
export { GeneralInfoForm };
